// ── Progress Sync — Firestore mirror of localStorage progress ─────────────────
// localStorage stays the synchronous source of truth at runtime so every read
// is instant and offline-safe. Firestore is the durable store that lets the
// same account see consistent progress across devices/browsers.
//
// Flow:
//   1. On sign-in (App.js), call `syncFromFirestore(uid)` once. It reads the
//      Firestore copy, merges with whatever localStorage already has, writes
//      the merged blob back to BOTH stores, and returns.
//   2. After step 1, call `setSyncUid(uid)`. From this point on, every call to
//      `scheduleFirestoreWrite()` (which `progress.js` does after each mutation)
//      schedules a single debounced upload of the current local blob.
//   3. On sign-out, call `setSyncUid(null)` to stop scheduled uploads.

import { doc, getDoc, setDoc, runTransaction, onSnapshot, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";

const STORAGE_KEY = "samajh-progress";
const WRITE_DEBOUNCE_MS = 500;

let currentUid = null;
let writeTimer = null;
// REALTIME-SYNC-APPLIED: live Firestore listener so an already-open device pulls
// changes made on another device and re-merges them (instead of only syncing at
// sign-in). Held here so we can tear it down on sign-out.
let unsubscribeSnapshot = null;
let onRemoteChangeCb = null;

function readLocal() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); }
  catch { return {}; }
}

function writeLocal(data) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {}
}

// Merge two progress blobs. Union of keys; for shared keys we keep the entry
// with the later timestamp. Chapter completion is sticky (once true, stays).
// Visit count sums across devices so the dashboard reflects total engagement.
function mergeProgress(local, remote) {
  const merged = { ...remote };

  // chapters
  const localChapters = local.chapters || {};
  const remoteChapters = remote.chapters || {};
  const mergedChapters = { ...remoteChapters };
  for (const [key, lc] of Object.entries(localChapters)) {
    const rc = remoteChapters[key];
    if (!rc) { mergedChapters[key] = lc; continue; }
    const localTs = lc.lastVisit || lc.firstVisit || "";
    const remoteTs = rc.lastVisit || rc.firstVisit || "";
    const winner = localTs > remoteTs ? lc : rc;
    mergedChapters[key] = {
      ...rc, ...winner,
      visits: (rc.visits || 0) + (lc.visits || 0),
      completed: rc.completed || lc.completed,
      completedAt: rc.completedAt || lc.completedAt || winner.completedAt,
      rating: winner.rating || rc.rating || lc.rating,
    };
  }
  merged.chapters = mergedChapters;

  // activeDays — union, sorted ascending
  const dayUnion = new Set([
    ...(remote.activeDays || []),
    ...(local.activeDays || []),
  ]);
  merged.activeDays = Array.from(dayUnion).sort();

  // conceptScores — keep the later attempt per key
  const localScores = local.conceptScores || {};
  const remoteScores = remote.conceptScores || {};
  const mergedScores = { ...remoteScores };
  for (const [key, ls] of Object.entries(localScores)) {
    const rs = remoteScores[key];
    if (!rs) { mergedScores[key] = ls; continue; }
    mergedScores[key] = (ls.timestamp || "") > (rs.timestamp || "") ? ls : rs;
  }
  merged.conceptScores = mergedScores;

  return merged;
}

// Fetch Firestore, merge with local, persist merged to both. Returns the merged
// blob. Idempotent — safe to call multiple times. If Firestore read fails we
// fall back to local data so the UI doesn't get stuck.
export async function syncFromFirestore(uid) {
  if (!uid) return readLocal();
  try {
    const ref = doc(db, "users", uid);
    const snap = await getDoc(ref);
    const remote = snap.exists() ? (snap.data().progress || {}) : {};
    const local = readLocal();
    const merged = mergeProgress(local, remote);
    writeLocal(merged);
    // Push the merged blob back so other devices see the same shape.
    // Use setDoc with merge so a profile-less doc is still created safely
    // (defensive — App.js should already have created the profile).
    await setDoc(ref, { progress: merged, progressUpdatedAt: serverTimestamp() }, { merge: true });
    return merged;
  } catch (e) {
    console.warn("[progress-sync] sync failed, using local only:", e);
    return readLocal();
  }
}

// Pass null on sign-out to stop scheduling writes (and tear down the realtime
// listener). Pass the new uid on sign-in (after syncFromFirestore resolves) to
// start syncing future mutations.
export function setSyncUid(uid) {
  currentUid = uid || null;
  if (!uid) {
    if (writeTimer) { clearTimeout(writeTimer); writeTimer = null; }
    stopRealtimeSync();
  }
}

// Called by progress.js after every mutation. Coalesces rapid writes into a
// single Firestore upload after WRITE_DEBOUNCE_MS of quiet. No-op if not
// currently signed in.
//
// CROSS-DEVICE-FIX-APPLIED: the upload runs inside a transaction that re-reads
// the remote blob and MERGES it with local before writing. Previously this did a
// blind `updateDoc({ progress: local })` — a stale device (one that hadn't pulled
// another device's changes) would overwrite and wipe the other device's progress.
// Merging on write makes every upload non-destructive: chapter keys only ever
// grow across devices.
export function scheduleFirestoreWrite() {
  if (!currentUid) return;
  if (writeTimer) clearTimeout(writeTimer);
  writeTimer = setTimeout(async () => {
    writeTimer = null;
    if (!currentUid) return;
    const ref = doc(db, "users", currentUid);
    let mergedOut = null;
    try {
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        const remote = snap.exists() ? (snap.data().progress || {}) : {};
        const local = readLocal();
        mergedOut = mergeProgress(local, remote);
        tx.set(ref, { progress: mergedOut, progressUpdatedAt: serverTimestamp() }, { merge: true });
      });
      // Persist the merged result locally so this device reflects anything the
      // transaction pulled in from other devices. Done AFTER commit (the tx
      // callback can re-run on contention, so side effects belong out here).
      if (mergedOut) writeLocal(mergedOut);
    } catch (e) {
      console.warn("[progress-sync] background write failed:", e);
    }
  }, WRITE_DEBOUNCE_MS);
}

// REALTIME-SYNC-APPLIED: subscribe to live changes on users/{uid} so a device
// that's already open picks up progress made on another device and re-merges it
// into local. `onRemoteChange` lets App.js trigger a UI refresh when that happens.
// Safe to call repeatedly — it tears down any prior listener first.
export function startRealtimeSync(uid, onRemoteChange) {
  stopRealtimeSync();
  if (!uid) return;
  onRemoteChangeCb = onRemoteChange || null;
  const ref = doc(db, "users", uid);
  unsubscribeSnapshot = onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) return;
      // Ignore events that are just our own un-acknowledged local writes echoing
      // back — only react to confirmed changes from the server (other devices).
      if (snap.metadata.hasPendingWrites) return;
      const remote = snap.data().progress || {};
      const local = readLocal();
      const merged = mergeProgress(local, remote);
      // Only touch local + repaint if the merge actually changed something,
      // so an echo of our own write doesn't cause a needless re-render loop.
      if (JSON.stringify(merged) !== JSON.stringify(local)) {
        writeLocal(merged);
        if (onRemoteChangeCb) onRemoteChangeCb();
      }
    },
    (e) => console.warn("[progress-sync] realtime listener error:", e)
  );
}

export function stopRealtimeSync() {
  if (unsubscribeSnapshot) { unsubscribeSnapshot(); unsubscribeSnapshot = null; }
  onRemoteChangeCb = null;
}
